import React, { useCallback, useMemo, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { twMerge } from 'tailwind-merge'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import { BackgroundJobStatusValue } from 'src/fetch/backgroundJobs/backgroundJobStatus'
import { Icon, Popover, T, VStack } from 'src/govwell-ui'
import useDisclosure from 'src/hooks/use-disclosure'
import { useIsGov, useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'

import {
  backgroundJobBarVariantClassName,
  getAggregateBackgroundJobBarContent,
  shouldIncludeRecordTypeInMultiJobDropdown,
} from './backgroundJobMessages'
import {
  getBackgroundJobPillNavigationDestination,
  navigateToOriginatingView,
} from './backgroundJobNavigation'
import BackgroundJobPillRow, { aiAssistantPillShellClassName } from './BackgroundJobPillRow'
import { useBackgroundJobs } from './BackgroundJobsProvider'
import type { TrackedBackgroundJob } from './types'

type Props = {
  /** Inline in TopBar vs fixed beside the floating AI Assistant pill. */
  placement?: 'inline' | 'floating'
}

const multiJobAggregateMaxWidthClassName = 'max-w-[280px]'

const BackgroundJobTopBarStatus = ({ placement = 'inline' }: Props) => {
  const isGov = useIsGov()
  const activeOrgId = useMyOrgId()
  const apolloClient = useApolloClient()
  const { notification } = useNotification()
  const { trackedJobs, cancelJob, dismissJob, getPillNavigationViewUuid } = useBackgroundJobs()
  const multiJobPopoverState = useDisclosure()
  const isTerminalNavigateInFlightRef = useRef(false)
  const [isTerminalNavigating, setIsTerminalNavigating] = useState(false)

  const handleCancelJob = useCallback(
    async (trackedJob: TrackedBackgroundJob, event: React.MouseEvent) => {
      event.stopPropagation()
      try {
        await cancelJob(trackedJob.id)
      } catch (error) {
        console.error(error)
        notification.error({ message: 'Could not cancel background job. Please try again.' })
      }
    },
    [cancelJob, notification]
  )

  const dismissTrackedJob = useCallback(
    async (trackedJob: TrackedBackgroundJob) => {
      try {
        await dismissJob(trackedJob.id)
      } catch (error) {
        console.error(error)
        notification.error({ message: 'Could not dismiss background job. Please try again.' })
      }
    },
    [dismissJob, notification]
  )

  const handleDismissJob = useCallback(
    async (trackedJob: TrackedBackgroundJob, event: React.MouseEvent) => {
      event.stopPropagation()
      await dismissTrackedJob(trackedJob)
    },
    [dismissTrackedJob]
  )

  const handleTerminalNavigateJob = useCallback(
    async (trackedJob: TrackedBackgroundJob) => {
      if (activeOrgId == null || isTerminalNavigateInFlightRef.current) {
        return
      }
      isTerminalNavigateInFlightRef.current = true
      setIsTerminalNavigating(true)
      try {
        await navigateToOriginatingView({
          apolloClient,
          job: trackedJob,
          organizationId: activeOrgId,
          viewUuid: getPillNavigationViewUuid(trackedJob.id),
        })
        await dismissJob(trackedJob.id)
      } catch (error) {
        console.error(error)
        notification.error({
          message: getBackgroundJobPillNavigationDestination(trackedJob.type)
            .navigationErrorMessage,
        })
      } finally {
        isTerminalNavigateInFlightRef.current = false
        setIsTerminalNavigating(false)
      }
    },
    [activeOrgId, apolloClient, dismissJob, getPillNavigationViewUuid, notification]
  )

  const singleJob = useMemo(
    () => (trackedJobs.length === 1 ? trackedJobs[0] : undefined),
    [trackedJobs]
  )

  const aggregateContent = useMemo(
    () => (trackedJobs.length >= 2 ? getAggregateBackgroundJobBarContent(trackedJobs) : null),
    [trackedJobs]
  )

  const singleJobCancelHandler = useMemo(
    () =>
      singleJob
        ? (event: React.MouseEvent) => {
            void handleCancelJob(singleJob, event)
          }
        : undefined,
    [handleCancelJob, singleJob]
  )

  const singleJobDismissHandler = useMemo(
    () =>
      singleJob
        ? (event: React.MouseEvent) => {
            void handleDismissJob(singleJob, event)
          }
        : undefined,
    [handleDismissJob, singleJob]
  )

  const singleJobTerminalNavigateHandler = useMemo(
    () =>
      singleJob
        ? () => {
            void handleTerminalNavigateJob(singleJob)
          }
        : undefined,
    [handleTerminalNavigateJob, singleJob]
  )

  const shouldIncludeRecordTypeInDropdown = useMemo(
    () => shouldIncludeRecordTypeInMultiJobDropdown(trackedJobs),
    [trackedJobs]
  )

  const multiJobPopoverContentProps = useMemo(
    () =>
      placement === 'floating'
        ? {
            align: 'end' as const,
            side: 'bottom' as const,
            className: 'w-max max-w-[calc(100vw-28px)] p-2',
          }
        : { align: 'end' as const, side: 'bottom' as const, className: 'w-max max-w-[340px] p-2' },
    [placement]
  )

  const multiJobDropdownRows = useMemo(
    () =>
      trackedJobs.map((trackedJob) => ({
        trackedJob,
        onCancel: (event: React.MouseEvent) => {
          void handleCancelJob(trackedJob, event)
        },
        onDropdownTerminalRowActivate: async () => {
          multiJobPopoverState.close()
          if (trackedJob.status === BackgroundJobStatusValue.Cancelled) {
            await dismissTrackedJob(trackedJob)
            return
          }
          await handleTerminalNavigateJob(trackedJob)
        },
      })),
    [
      dismissTrackedJob,
      handleCancelJob,
      handleTerminalNavigateJob,
      multiJobPopoverState.close,
      trackedJobs,
    ]
  )

  const pill = useMemo(() => {
    if (singleJob) {
      return (
        <BackgroundJobPillRow
          isTerminalNavigateDisabled={isTerminalNavigating}
          job={singleJob}
          layout="pill"
          onCancel={singleJobCancelHandler}
          onDismiss={singleJobDismissHandler}
          onTerminalNavigate={singleJobTerminalNavigateHandler}
        />
      )
    }

    if (!aggregateContent) {
      return null
    }

    return (
      <Popover
        isOpen={multiJobPopoverState.isOpen}
        onClose={multiJobPopoverState.close}
        onOpen={multiJobPopoverState.open}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-expanded={multiJobPopoverState.isOpen}
            aria-label={aggregateContent.message}
            className={twMerge(
              'relative inline-flex min-w-0 shrink-0 cursor-pointer items-center gap-1.5 border whitespace-nowrap',
              multiJobAggregateMaxWidthClassName,
              aiAssistantPillShellClassName,
              '!px-2.5',
              backgroundJobBarVariantClassName(aggregateContent.variant)
            )}
          >
            {aggregateContent.showSpinner && (
              <Icon icon="FaSpinnerThird" className="size-3.5 shrink-0 animate-spin" />
            )}
            <T.Body className="min-w-0 flex-1 truncate text-[11px] leading-none font-medium">
              {aggregateContent.message}
            </T.Body>
            <Icon icon="FaChevronDown" className="size-2 shrink-0 opacity-75" />
          </button>
        </Popover.Trigger>
        <Popover.Content {...multiJobPopoverContentProps}>
          <VStack gap={1}>
            {multiJobDropdownRows.map(({ trackedJob, onCancel, onDropdownTerminalRowActivate }) => (
              <BackgroundJobPillRow
                key={trackedJob.id}
                isTerminalNavigateDisabled={isTerminalNavigating}
                job={trackedJob}
                layout="dropdown"
                onCancel={onCancel}
                onDropdownTerminalRowActivate={onDropdownTerminalRowActivate}
                shouldIncludeRecordTypeInLocation={shouldIncludeRecordTypeInDropdown}
              />
            ))}
          </VStack>
        </Popover.Content>
      </Popover>
    )
  }, [
    aggregateContent,
    multiJobDropdownRows,
    multiJobPopoverContentProps,
    shouldIncludeRecordTypeInDropdown,
    multiJobPopoverState.isOpen,
    multiJobPopoverState.close,
    multiJobPopoverState.open,
    singleJob,
    singleJobCancelHandler,
    singleJobDismissHandler,
    singleJobTerminalNavigateHandler,
    isTerminalNavigating,
  ])

  if (!isGov || !pill) {
    return null
  }

  if (placement === 'floating') {
    return (
      <span className="pointer-events-auto absolute top-1/2 right-full mr-2 max-w-[min(400px,calc(100vw-180px))] shrink-0 -translate-y-1/2">
        {pill}
      </span>
    )
  }

  return <span className="pointer-events-auto shrink-0">{pill}</span>
}

export default React.memo(BackgroundJobTopBarStatus)
